import { useRef } from "react";
import { useTranslation } from "react-i18next";

import { Input } from "../../../../../components/ui/uikit/input";

// ---------------------------------------------------------------------------
// SsnInput
//
// A controlled 3-segment split SSN/ITIN input that stores and returns a single
// canonical digits-only string (0–9 digits, no separators). The three segments
// (first / middle / last → 3 / 2 / 4 digits) are displayed as separate Input
// boxes separated by a visible "-" dash, formatting to XXX-XX-XXXX.
//
// Auto-advances focus first → middle at 3 digits, and middle → last at 2 digits.
// autoComplete is off on every segment since this is PII.
// ---------------------------------------------------------------------------

interface SsnInputProperties {
  /** Canonical digits string. May arrive formatted; non-digits are stripped internally. */
  value: string;
  onChange: (digits: string) => void;
  onBlur?: () => void;
  /** Base accessible name, e.g. "SSN or ITIN". Each segment gets a suffix appended. */
  ariaLabel: string;
  required?: boolean;
  /** When true, aria-invalid is set on each segment. */
  invalid?: boolean;
  disabled?: boolean;
  /** Used to derive stable input ids: ${idBase}-first / -middle / -last. */
  idBase: string;
}

function buildDigits(first: string, middle: string, last: string): string {
  return (first + middle + last).slice(0, 9);
}

export function SsnInput({
  value,
  onChange,
  onBlur,
  ariaLabel,
  required,
  invalid,
  disabled,
  idBase,
}: SsnInputProperties) {
  const { t } = useTranslation();
  const digits = value.replaceAll(/\D/g, "").slice(0, 9);
  const first = digits.slice(0, 3);
  const middle = digits.slice(3, 5);
  const last = digits.slice(5, 9);

  const middleReference = useRef<HTMLInputElement>(null);
  const lastReference = useRef<HTMLInputElement>(null);

  function handleFirstChange(event: React.ChangeEvent<HTMLInputElement>) {
    const raw = event.target.value.replaceAll(/\D/g, "").slice(0, 3);
    onChange(buildDigits(raw, middle, last));
    if (raw.length === 3) {
      middleReference.current?.focus();
    }
  }

  function handleMiddleChange(event: React.ChangeEvent<HTMLInputElement>) {
    const raw = event.target.value.replaceAll(/\D/g, "").slice(0, 2);
    onChange(buildDigits(first, raw, last));
    if (raw.length === 2) {
      lastReference.current?.focus();
    }
  }

  function handleLastChange(event: React.ChangeEvent<HTMLInputElement>) {
    const raw = event.target.value.replaceAll(/\D/g, "").slice(0, 4);
    onChange(buildDigits(first, middle, raw));
  }

  const ariaInvalid = invalid || undefined;
  const sharedProperties = {
    type: "text" as const,
    inputMode: "numeric" as const,
    autoComplete: "off",
    disabled,
    "aria-required": required,
    "aria-invalid": ariaInvalid,
    onBlur,
  };

  return (
    <div className="flex items-center">
      <Input
        {...sharedProperties}
        id={`${idBase}-first`}
        value={first}
        onChange={handleFirstChange}
        maxLength={3}
        placeholder={t("review.customer.numberPlaceholder.three")}
        className="w-12"
        aria-label={`${ariaLabel} first 3 digits`}
      />
      <span className="text-muted-foreground w-2 shrink-0 select-none text-center text-xs" aria-hidden="true">
        -
      </span>
      <Input
        {...sharedProperties}
        id={`${idBase}-middle`}
        ref={middleReference}
        value={middle}
        onChange={handleMiddleChange}
        maxLength={2}
        placeholder={t("review.customer.numberPlaceholder.two")}
        className="w-10"
        aria-label={`${ariaLabel} middle 2 digits`}
      />
      <span className="text-muted-foreground w-2 shrink-0 select-none text-center text-xs" aria-hidden="true">
        -
      </span>
      <Input
        {...sharedProperties}
        id={`${idBase}-last`}
        ref={lastReference}
        value={last}
        onChange={handleLastChange}
        maxLength={4}
        placeholder={t("review.customer.numberPlaceholder.four")}
        className="w-14"
        aria-label={`${ariaLabel} last 4 digits`}
      />
    </div>
  );
}
